import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { Subject, Observable } from 'rxjs';
import { APIUrl } from '../constants';

export interface BulkSearchProgressUpdate {
  BatchId: string;
  RowIndex: number;
  SearchTerm: string;
  CartItem: any;
  Success: boolean;
  ErrorMessage: string;
  CompletedCount: number;
  TotalCount: number;
  ProgressPercent: number;
  CurrentProductCode?: string;
  Status?: string;
  Message?: string;
}

export interface BulkSearchCompleteMessage {
  BatchId: string;
  TotalProcessed: number;
  SuccessCount: number;
  FailedCount: number;
}

// ===== MASS EMAIL PROGRESS INTERFACES =====
export interface MassEmailProgressUpdate {
  BatchId: string;
  AgentID: number;
  AgentName: string;
  AgentEmail: string;
  Success: boolean;
  ErrorMessage: string;
  CompletedCount: number;
  TotalCount: number;
  ProgressPercent: number;
  SuccessCount: number;
  FailedCount: number;
}

export interface MassEmailCompleteMessage {
  BatchId: string;
  TotalProcessed: number;
  SuccessCount: number;
  FailedCount: number;
}

// ===== INTRASTAT PROGRESS INTERFACES =====
export interface IntrastatProgressUpdate {
  BatchId: string;
  Phase: string;           // Current phase: 'FETCH', 'HSCODE', 'GENERATE', 'COMPLETE'
  PhaseIndex: number;      // 1, 2, 3, 4
  TotalPhases: number;     // Total number of phases
  CurrentItem: number;     // Current item in this phase
  TotalItems: number;      // Total items in this phase
  ProgressPercent: number; // Overall progress percent
  PhasePercent: number;    // Progress within current phase
  StatusMessage: string;   // Human readable status
  DetailMessage: string;   // Additional detail (e.g., invoice number)
}

export interface IntrastatCompleteMessage {
  BatchId: string;
  TotalInvoices: number;
  TotalProducts: number;
  TotalDeclarations: number;
  Success: boolean;
  ErrorMessage: string;
}

// ===== ORDER PROCESSING PROGRESS INTERFACES =====
export interface OrderProcessingProgressUpdate {
  BatchId: string;
  BatchIndex: number;       // 1-based index of current batch
  TotalBatches: number;
  StepIndex: number;        // 1-based index of current step within batch
  TotalSteps: number;       // Total steps per batch (11)
  StepName: string;         // Step identifier for i18n
  Message: string;          // Human readable message
  ProgressPercent: number;  // 0-100 per-batch progress (resets each batch)
  OverallPercent: number;   // 0-100 overall progress across all batches
}

@Injectable({
  providedIn: 'root'
})
export class SignalRService {
  private hubConnection: signalR.HubConnection | null = null;
  private connectionId: string | null = null;

  // Subjects for broadcasting events - Bulk Search
  private searchItemCompleted = new Subject<BulkSearchProgressUpdate>();
  private batchCompleted = new Subject<BulkSearchCompleteMessage>();
  private connected = new Subject<string>();

  // Subjects for broadcasting events - Mass Email
  private massEmailProgress = new Subject<MassEmailProgressUpdate>();
  private massEmailComplete = new Subject<MassEmailCompleteMessage>();

  // Subjects for broadcasting events - Intrastat
  private intrastatProgress = new Subject<IntrastatProgressUpdate>();
  private intrastatComplete = new Subject<IntrastatCompleteMessage>();

  // Subjects for broadcasting events - Order Processing
  private orderProcessingProgress = new Subject<OrderProcessingProgressUpdate>();

  // Observables for components to subscribe to - Bulk Search
  public searchItemCompleted$: Observable<BulkSearchProgressUpdate> = this.searchItemCompleted.asObservable();
  public batchCompleted$: Observable<BulkSearchCompleteMessage> = this.batchCompleted.asObservable();
  public connected$: Observable<string> = this.connected.asObservable();

  // Observables for components to subscribe to - Mass Email
  public massEmailProgress$: Observable<MassEmailProgressUpdate> = this.massEmailProgress.asObservable();
  public massEmailComplete$: Observable<MassEmailCompleteMessage> = this.massEmailComplete.asObservable();

  // Observables for components to subscribe to - Intrastat
  public intrastatProgress$: Observable<IntrastatProgressUpdate> = this.intrastatProgress.asObservable();
  public intrastatComplete$: Observable<IntrastatCompleteMessage> = this.intrastatComplete.asObservable();

  // Observables for components to subscribe to - Order Processing
  public orderProcessingProgress$: Observable<OrderProcessingProgressUpdate> = this.orderProcessingProgress.asObservable();

  constructor() { }

  /**
   * Get the API URL for SignalR hub
   */
  private getHubUrl(): string {
    // Use APIUrl from constants (same as B2BService uses)
    return APIUrl + '/hubs/bulksearch';
  }

  /**
   * Connect to the BulkSearch SignalR hub with retry logic
   */
  async connect(maxRetries: number = 2, retryDelayMs: number = 1000): Promise<string> {
    if (this.hubConnection && this.hubConnection.state === signalR.HubConnectionState.Connected) {
      return this.connectionId!;
    }

    // Clean up any existing connection first
    if (this.hubConnection) {
      try {
        await this.hubConnection.stop();
      } catch { /* ignore cleanup errors */ }
      this.hubConnection = null;
    }

    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(this.getHubUrl())
      .withAutomaticReconnect([0, 1000, 2000, 5000]) // Retry delays: 0s, 1s, 2s, 5s
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    // Set up event handlers
    this.hubConnection.on('Connected', (connectionId: string) => {
      console.log('✅ SignalR Connected:', connectionId);
      this.connectionId = connectionId;
      this.connected.next(connectionId);
    });

    this.hubConnection.on('SearchItemCompleted', (update: BulkSearchProgressUpdate) => {
      this.searchItemCompleted.next(update);
    });

    this.hubConnection.on('BatchCompleted', (message: BulkSearchCompleteMessage) => {
      this.batchCompleted.next(message);
    });

    // Mass Email event handlers
    this.hubConnection.on('MassEmailProgress', (update: MassEmailProgressUpdate) => {
      console.log('🔔 SignalR Hub received MassEmailProgress:', update);
      this.massEmailProgress.next(update);
    });

    this.hubConnection.on('MassEmailComplete', (message: MassEmailCompleteMessage) => {
      console.log('🔔 SignalR Hub received MassEmailComplete:', message);
      this.massEmailComplete.next(message);
    });

    // Intrastat event handlers
    this.hubConnection.on('IntrastatProgress', (update: IntrastatProgressUpdate) => {
      console.log('📊 SignalR Hub received IntrastatProgress:', update);
      this.intrastatProgress.next(update);
    });

    this.hubConnection.on('IntrastatComplete', (message: IntrastatCompleteMessage) => {
      console.log('📊 SignalR Hub received IntrastatComplete:', message);
      this.intrastatComplete.next(message);
    });

    // Order Processing event handler
    this.hubConnection.on('OrderProcessingProgress', (update: OrderProcessingProgressUpdate) => {
      this.orderProcessingProgress.next(update);
    });

    this.hubConnection.onreconnecting((error) => {
      console.warn('⚠️ SignalR Reconnecting:', error);
    });

    this.hubConnection.onreconnected((connectionId) => {
      console.log('✅ SignalR Reconnected:', connectionId);
      this.connectionId = connectionId!;
    });

    this.hubConnection.onclose((error) => {
      console.log('❌ SignalR Disconnected:', error);
      this.connectionId = null;
    });

    // Start connection with retry logic
    let lastError: any = null;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        if (attempt > 0) {
          console.log(`🔄 SignalR connection retry ${attempt}/${maxRetries}...`);
          await this.delay(retryDelayMs);
        }

        await this.hubConnection.start();
        console.log('✅ SignalR Hub Started');

        // Connection ID is sent via the 'Connected' event, but we can also get it here
        this.connectionId = this.hubConnection.connectionId;
        return this.connectionId!;
      } catch (error) {
        lastError = error;
        console.warn(`⚠️ SignalR connection attempt ${attempt + 1} failed:`, error);
      }
    }

    console.error('❌ SignalR Connection Error after retries:', lastError);
    throw lastError;
  }

  /**
   * Helper for async delay
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Disconnect from the hub
   */
  async disconnect(): Promise<void> {
    if (this.hubConnection) {
      await this.hubConnection.stop();
      this.hubConnection = null;
      this.connectionId = null;
    }
  }

  /**
   * Get the current connection ID
   */
  getConnectionId(): string | null {
    return this.connectionId;
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.hubConnection?.state === signalR.HubConnectionState.Connected;
  }
}

